import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Common/Button';
import { Search, MessageSquarePlus, MapPin, Filter, Grid3x3 } from 'lucide-react';

const PARTNER = [
  {
    name: 'guidle.com',
    url: 'https://www.guidle.com',
    description: 'Schweizer Veranstaltungsplattform mit redaktionell geprüften Inhalten',
  },
  {
    name: 'badiportal.ch',
    url: 'https://www.badiportal.ch',
    description: 'Das umfassende Schweizer Bäderverzeichnis',
  },
  {
    name: 'schweizerfeuerstellen.ch',
    url: 'https://schweizerfeuerstellen.ch',
    description: 'Feuerstellen und Grillplätze in der Schweiz',
  },
  {
    name: 'kinderwanderwege.ch',
    url: 'https://kinderwanderwege.ch',
    description: 'Kinderfreundliche Wanderwege in der Schweiz',
  },
];

const GESTALTUNG = [
  {
    name: 'Hryhorii Slipchenko',
    url: 'https://www.upwork.com/freelancers/~012d0ae2da94485f25',
    description: 'UX/UI Designer',
  },
  {
    name: 'sasakitiller.ch',
    url: 'https://sasakitiller.ch/',
    description: 'Corporate Design',
  },
];

const DATENQUELLEN = [
  {
    name: 'eventfrog.ch',
    url: 'https://eventfrog.ch',
    description: 'Veranstaltungen und Events in der Schweiz',
  },
];

const SourceCard = ({ name, url, description }) => (
  <a
    className="source-card"
    href={url}
    target="_blank"
    rel="noopener noreferrer"
  >
    <div className="source-card-body">
      <h4 className="card-title source-card-title">{name}</h4>
      <p className="card-text source-card-text">{description}</p>
    </div>
  </a>
);

const UeberUns = () => {
  const navigate = useNavigate();
  const aboutSectionRef = useRef(null);

  return (
    <div className="landing-page">
      {/* About Section */}
      <div className="about-section" ref={aboutSectionRef}>
        <div className="about-container">
          <h1 className="section-title">
            Über dieses Projekt
          </h1>
          <p className="hero-subtitle">
            Wir verbinden Familien mit passenden Freizeitangeboten in der Deutschschweiz
          </p>
          
          <div className="about-grid">
            <div className="about-card">
              <h3 className="card-title">Was wir bieten</h3>
              <p className="card-text">
                Eine umfassende Plattform zur Entdeckung von Freizeitaktivitäten für Kinder in der Deutschschweiz. 
                Mit intelligenten Filtern nach Alter, Kosten und Fahrzeit finden Sie passende Aktivitäten – inklusive 
                direkter ÖV-Routenplanung ab Ihrem Wohnort.
              </p>
            </div>

            <div className="about-card">
              <h3 className="card-title">Unsere Mission</h3>
              <p className="card-text">
                Wir möchten Grosseltern, Eltern und Betreuungspersonen dabei unterstützen, qualitativ hochwertige und 
                altersgerechte Freizeitangebote in ihrer Nähe zu finden. Unser Ziel ist es, Grosskindern unvergessliche Erlebnisse 
                zu ermöglichen und Familien zu inspirieren.
              </p>
            </div>
          </div>
          
          {/* Main Action Buttons */}
          <div className="hero-actions">
            <Button
              size="lg"
              className="primary-action"
              onClick={() => navigate('/')}
            >
              <Search className="icon-mr" size={20} />
              Aktivitäten finden
            </Button>
            
            <Button
              size="lg"
              variant="outline"
              className="secondary-action"
              onClick={() => navigate('/activities/new')}
            >
              <MessageSquarePlus className="icon-mr" size={20} />
              Aktivität mitteilen
            </Button>
          </div>

          {/* Features */}
          <h3 className="features-title">Funktionen</h3>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">
                <Filter size={32} />
              </div>
              <h3 className="feature-title">Umfangreiche Filter</h3>
              <p className="feature-text">
                Filtern Sie nach Alter, Kategorie, Wetter, Kosten und vielem mehr
              </p>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <MapPin size={32} />
              </div>
              <h3 className="feature-title">Kartenansicht</h3>
              <p className="feature-text">
                Visualisieren Sie den Weg zu den Aktivitäten auf Google Maps mit direkter Routenplanung
              </p>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <Grid3x3 size={32} />
              </div>
              <h3 className="feature-title">Übersichtliche Darstellung</h3>
              <p className="feature-text">
                Navigieren Sie durch eine benutzerfreundliche Oberfläche mit klaren Kategorien und Empfehlungen
              </p>
            </div>
          </div>
          
          {/* Hero Section */}
          <div className="hero-section">
            <h3 className="features-title">Team</h3>
            <div className="hero-content">
              {/* Teamfoto: fuellt den freien Raum zwischen Header und Titel */}
              <figure className="hero-figure">
                <img
                  className="hero-image"
                  src="/team-grosskinder-aktiv-1600.jpg"
                  srcSet="/team-grosskinder-aktiv-900.jpg 900w, /team-grosskinder-aktiv-1600.jpg 1600w"
                  sizes="(min-width: 900px) 56rem, 100vw"
                  width="1600"
                  height="1143"
                  alt="Das Team von Grosskinder Aktiv – sechs Grosseltern gehen nebeneinander auf die Kamera zu"
                />
                <figcaption className="hero-caption">
                  Von links: Hermann Pätzold, Judith von Wyl, Peter Maly, Elsbeth Wobmann,
                  Monika German und Heini Zingg.
                </figcaption>
              </figure>
            </div>
          </div>
          
          {/* Datenquellen und Partner */}
          <div className="sources-section">
            <h2 className="sources-title" style={{ textAlign: "center" }}>Unsere Datenquellen und Partner</h2>
            <p className="card-text sources-intro">
              «Grosskinder Aktiv» aggregiert Inhalte aus verschiedenen Quellen, um euch ein möglichst
            breites Angebot zu bieten. Alle übernommenen Inhalte werden von unserem Team geprüft und
            moderiert – wir nehmen nur Einträge auf, die dem Erlebnis der Grosskinder dienen und mit den
            Zielen der Plattform übereinstimmen.
            </p>

            <h2 className="sources-title">Partner</h2>
            <div className="sources-list">
              {PARTNER.map((quelle) => (
                <SourceCard key={quelle.name} {...quelle} />
              ))}
            </div>

            <h2 className="sources-title">Datenquellen</h2>
            <div className="sources-list">
              {DATENQUELLEN.map((quelle) => (
                <SourceCard key={quelle.name} {...quelle} />
              ))}
            </div>

            <p className="feature-text sources-note">
              Alle Einträge verweisen direkt auf die Originalquelle. Grosskinder Aktiv empfiehlt,
              die Angaben vor einem Besuch auf der jeweiligen Website zu überprüfen.
            </p>
          </div>

          {/* Gestaltung */}
          <div className="sources-section">
            <h2 className="sources-title">Gestaltung</h2>
            <div className="sources-list">
              {GESTALTUNG.map((eintrag) => (
                <SourceCard key={eintrag.name} {...eintrag} />
              ))}
            </div>
            <p className="feature-text sources-note">
              Das Erscheinungsbild und die Benutzerführung von «Grosskinder Aktiv» sind mit
              professioneller Unterstützung entstanden.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default UeberUns;
